import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { majorContractsService } from '@shared/services/majorContracts';
import { savePersonFormReturnContext } from '@utils/personFormReturnStorage';

const OVERLAY_ICON_IDS = ['contractDocumentNum', 'contractWord', 'contractPersonCollect'];

export default function PersonContractHeading({ activeForm, setActiveForm, person, onClose, contractType, setContractType, selectedMainContract, setSelectedMainContract, onMainContractsLoaded, onNewIconClick, onOverlayIconClick, onActivateClick, onConfirmClick, onEraseClick, onRemoveClick, onRefuseClick, onExportClick, onMailSentClick, isEditMode = false, isCreateModeActive = false, editingContractId = null, onCancelAddMode, onCancelEditMode, contractNo = null, selectedContractId = null, selectedContractPositionTypeId = null }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [mainContracts, setMainContracts] = useState([]);
    const [mainContractsLoading, setMainContractsLoading] = useState(false);
    const [showDocumentToContract, setShowDocumentToContract] = useState(false);


    useEffect(() => {
        const id = person?.recordId ?? person?.empId ?? person?.id;
        if (!id) return;
        const fetchMainContracts = async () => {
            const data = await majorContractsService.getMainContractsByPersonId(id);
            setMainContracts(data);
            onMainContractsLoaded?.(data);
        };
        fetchMainContracts();
    }, [person?.recordId, person?.empId, person?.id]);

    const icons = [
        { id: 'contractNew', image: '/images/empContract/new.png', label: t('staff.register.actionIcons.new') },
        { id: "compensation", image: '/images/narIcons/compensation.png', label: t('staff.register.actionIcons.compensations') },
        { id: 'contractActivation', image: '/images/empContract/contractActivation.png', label: t('staff.register.actionIcons.activation') },
        { id: 'contractAccept', image: '/images/empContract/accept-icon.png', label: t('staff.register.actionIcons.confirmationByNRA') },
        { id: 'contractErase', image: '/images/empContract/erasure.png', label: t('staff.register.actionIcons.revocation') },
        { id: 'contractRemove', image: '/images/empContract/trash.png', label: t('staff.register.actionIcons.delete') },
        { id: 'contractNapReject', image: '/images/empContract/napReject.png', label: t('staff.register.actionIcons.refusalByNRA') },
        { id: 'contractWord', image: '/images/empContract/word.png', label: t('staff.register.actionIcons.document') },
        { id: 'contractDocuments', image: '/images/empContract/documentos.png', label: t('staff.register.actionIcons.description') },
        { id: 'contractDocumentNum', image: '/images/empContract/document-num-icon.png', label: t('staff.register.actionIcons.changeDocNumber') },
        { id: 'contractExport', image: '/images/empContract/export-icon.png', label: t('staff.register.actionIcons.exportIDM') },
        { id: 'contractMailSent', image: '/images/empContract/mailsent.png', label: t('staff.register.actionIcons.emailGeneration') },
        { id: 'contractPersonCollect', image: '/images/empContract/Global-Network.png', label: t('staff.register.actionIcons.collectData') },
    ];
    const calcIcon = { id: 'contractCalc', image: '/images/empContract/calculator-icon.png', label: t('staff.register.actionIcons.calculateSalary') };

    // Show document to contract select only for specific contract types
    // Do not clear selectedMainContract when hiding: loadContractById sets contractType from the
    // loaded row (e.g. labor) while the user may still be in termination flow with a base contract
    // chosen — clearing would wipe restore state and cause a wrong row to load.
    useEffect(() => {
        if (contractType === 'additionalAgreement' || contractType === 'termination' || contractType === 'employerChange') {
            setShowDocumentToContract(true);
        } else {
            setShowDocumentToContract(false);
        }
    }, [contractType]);

    const contractTypeLabel = t(`persons.personContract.${contractType}`);
    const personName = [person?.name, person?.fathersName, person?.surname].filter(Boolean).join(' ');

    return (
        <>
            <h4 className="form-subtitle">
                {t('persons.personContract.titleFormat', {
                    type: contractTypeLabel,
                    name: personName
                })}
            </h4>
            <div className="person-action-icons" style={{ position: 'relative' }}>
                <div className="employee-action-icons">
                    {isCreateModeActive && !editingContractId ? (
                        <>
                            {/* Show only New icon and Cancel button in create new mode (not edit mode) */}
                            <div
                                className={`action-icon ${activeForm === 'contractNew' ? 'active' : ''}`}
                                onClick={() => { 
                                    if (onNewIconClick) {
                                        onNewIconClick();
                                    }
                                }}
                                title={t('common.add')}
                            >
                                <img src="/images/empContract/new.png" alt="New" />
                            </div>
                            {onCancelAddMode && (
                                <button className="form-btn btn-red"
                                    onClick={onCancelAddMode} 
                                    title={t('staff.register.actionIcons.cancelAddMode')}
                                >
                                    {t('staff.register.actionIcons.cancelAddMode')}
                                </button>
                            )}
                        </>
                    ) : editingContractId ? (
                        <>
                            {/* Show edit icon and Cancel button in edit mode */}
                            <div
                                className="action-icon"
                                title={t('common.edit')}
                            >
                                {/* Icon placeholder - to be added later */}
                                <img src="/images/icons/editDocument.png" alt="Edit" />
                            </div>
                            {onCancelEditMode && (
                                <button className="form-btn btn-red"
                                    onClick={onCancelEditMode} 
                                    title={t('staff.register.actionIcons.cancelEditMode')}
                                >
                                    {t('staff.register.actionIcons.cancelEditMode')}
                                </button>
                            )}
                        </>
                    ) : (
                        <>
                            {/* Show all icons when not in add mode or edit mode */}
                            {icons.map(icon => {
                                const isCompensationIcon = icon.id === 'compensation';
                                if (isCompensationIcon && contractType !== 'termination') {
                                    return null;
                                }

                                const shouldHideIconWithoutPositionType =
                                    (icon.id === 'contractExport' || icon.id === 'contractMailSent') &&
                                    selectedContractPositionTypeId == null;

                                if (shouldHideIconWithoutPositionType) {
                                    return null;
                                }

                                return (
                                <div
                                    key={icon.id}
                                    className={`action-icon ${activeForm === icon.id ? 'active' : ''}`}
                                    onClick={() => { 
                                        if (icon.id === 'contractNew' && onNewIconClick) {
                                            onNewIconClick();
                                        } else if (icon.id === 'compensation') {
                                            const mainContractsSnapshot = Array.isArray(mainContracts)
                                                ? [...mainContracts]
                                                : [];
                                            const returnPerson = {
                                                ...person,
                                                recordId: person?.recordId,
                                                empId: person?.empId,
                                                fullName: person?.fullName,
                                                personId: person?.personId ?? person?.id,
                                                personContractType: contractType,
                                                selectedMainContract: selectedMainContract || '',
                                                selectedContractId: editingContractId || selectedContractId || null,
                                                mainContracts: mainContractsSnapshot,
                                            };
                                            savePersonFormReturnContext({
                                                returnPath: '/personnel/employment-contracts/register',
                                                person: returnPerson,
                                                mainContracts: mainContractsSnapshot,
                                                selectedRecord: {
                                                    id: returnPerson.recordId,
                                                    empId: returnPerson.empId,
                                                    fullName: returnPerson.fullName,
                                                    personId: returnPerson.personId,
                                                },
                                                activeForm: 'personContract',
                                                showRegisterIcons: true,
                                                selectedMainContract: selectedMainContract || '',
                                                selectedContractId: editingContractId || selectedContractId || null,
                                            });
                                            onClose?.();
                                            navigate('/personnel/settings/labor-office-nra/termination/compensation', {
                                                state: {
                                                    person: returnPerson,
                                                    returnPath: '/personnel/employment-contracts/register',
                                                    mainContracts: mainContractsSnapshot,
                                                },
                                            });
                                        } else if (icon.id === 'contractActivation' && onActivateClick) {
                                            onActivateClick();
                                        } else if (icon.id === 'contractAccept' && onConfirmClick) {
                                            onConfirmClick();
                                        } else if (icon.id === 'contractErase' && onEraseClick) {
                                            onEraseClick();
                                        } else if (icon.id === 'contractRemove' && onRemoveClick) {
                                            onRemoveClick();
                                        } else if (icon.id === 'contractNapReject' && onRefuseClick) {
                                            onRefuseClick();
                                        } else if (icon.id === 'contractExport' && onExportClick) {
                                            onExportClick();
                                        } else if (icon.id === 'contractMailSent' && onMailSentClick) {
                                            onMailSentClick();
                                        } else if (OVERLAY_ICON_IDS.includes(icon.id) && onOverlayIconClick) {
                                            onOverlayIconClick(icon.id);
                                        } else {
                                            setActiveForm(icon.id);
                                        }
                                    }}
                                    title={icon.label}
                                >
                                    <img src={icon.image} alt={icon.label} />
                                </div>
                                );
                            })}
                        </>
                    )}
                </div>
                {!isCreateModeActive && (
                    <div
                        className={`action-icon ${activeForm === calcIcon.id ? 'active' : ''}`}
                        onClick={() => { onOverlayIconClick?.(calcIcon.id); }}
                        title={calcIcon.label}
                        style={{ position: 'absolute', right: '0', top: '50%', transform: 'translateY(-50%)', height: '35px', width: 'auto', minWidth: '60px' }}
                    >
                        <img src={calcIcon.image} alt={calcIcon.label} />
                    </div>
                )}
            </div>
            <div className="inline-radio-group">
                <label className="radio-item">
                    <input
                        type='radio'
                        name="contractType"
                        value="laborContract"
                        checked={contractType === 'laborContract'}
                        onChange={(e) => setContractType(e.target.value)}
                        disabled={!isEditMode}
                    />
                    <span>{t('persons.personContract.laborContract')}</span>
                </label>
                <label className="radio-item">
                    <input
                        type='radio'
                        name="contractType"
                        value="additionalLaborContract"
                        checked={contractType === 'additionalLaborContract'}
                        onChange={(e) => setContractType(e.target.value)}
                        disabled={!isEditMode}
                    />
                    <span>{t('persons.personContract.additionalLaborContract')}</span>
                </label>
                <label className="radio-item">
                    <input
                        type='radio'
                        name="contractType"
                        value="additionalAgreement"
                        checked={contractType === 'additionalAgreement'}
                        onChange={(e) => setContractType(e.target.value)}
                        disabled={!isEditMode}
                    />
                    <span>{t('persons.personContract.additionalAgreement')}</span>
                </label>
                <label className="radio-item">
                    <input
                        type='radio'
                        name="contractType"
                        value="termination"
                        checked={contractType === 'termination'}
                        onChange={(e) => setContractType(e.target.value)}
                        disabled={!isEditMode}
                    />
                    <span>{t('persons.personContract.termination')}</span>
                </label>
                <label className="radio-item">
                    <input
                        type='radio'
                        name="contractType"
                        value="employerChange"
                        checked={contractType === 'employerChange'}
                        onChange={(e) => setContractType(e.target.value)}
                        disabled={!isEditMode}
                    />
                    <span>{t('persons.personContract.employerChange')}</span>
                </label>
            </div>
            {showDocumentToContract && (
                <div className="flex-row position-center">
                    <span> Документ към договор  </span>
                    <select
                        className="small-select"
                        style={{ width: "25%" }}
                        value={selectedMainContract}
                        onChange={(e) => setSelectedMainContract(e.target.value)}
                        disabled={mainContractsLoading || !isEditMode}
                    >
                        <option value="">{t('common.select') || 'Select'}</option>
                        {mainContracts.map(contract => (
                            <option key={contract.contractId} value={contract.contractId}>
                                {contract.name}
                            </option>
                        ))}
                    </select>
                </div>
            )}
        </>
    );
}